/**
 * ZK Time Clock Frontend Service
 *
 * API client for ZK device integration endpoints.
 */

import apiService from '../../../services/apiService';

// ============ Types ============

export interface ZkDevice {
  id: string;
  deviceName: string;
  deviceSerial: string | null;
  ipAddress: string;
  port: number;
  status: 'connected' | 'disconnected' | 'error';
  isEnabled: boolean;
  timezone: string;
  lastSyncAt: string | null;
  lastRecordTime: string | null;
  syncInterval: number;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ZkDeviceInfo {
  serialNumber?: string;
  deviceName?: string;
  platform?: string;
  firmware?: string;
  macAddress?: string;
  userCount?: number;
  logCount?: number;
}

export interface ZkUser {
  uid: number;
  name: string;
  password?: string;
  role: number;
  cardNo?: number;
  userId: string;
}

export interface ZkEmployeeMapping {
  id: string;
  deviceId: string;
  zkUserId: string;
  zkUserName: string | null;
  employeeId: string | null;
  employeeCode: string | null;
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    employeeId: string;
    firstName: string;
    lastName: string;
    department: string | null;
    status: string;
  } | null;
  device?: {
    id: string;
    deviceName: string;
  };
}

export interface ZkRawAttendance {
  id: string;
  deviceId: string;
  zkUserId: string;
  punchTime: string;
  punchType: number | null;
  punchTypeLabel: string | null;
  verifyType: number | null;
  verifyTypeLabel: string | null;
  workCode: number | null;
  syncedToHr: boolean;
  syncedAt: string | null;
  companyId: string;
  createdAt: string;
  device?: {
    id: string;
    deviceName: string;
  };
  employeeMapping?: {
    employeeId: string | null;
    employeeCode: string | null;
    zkUserName: string | null;
    employee?: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
  } | null;
}

export interface ZkSyncHistory {
  id: string;
  deviceId: string | null;
  syncType: 'scheduled' | 'manual';
  status: 'running' | 'completed' | 'error';
  recordsFetched: number;
  recordsNew: number;
  recordsProcessed: number;
  startTime: string;
  endTime: string | null;
  errorMessage: string | null;
  companyId: string;
  createdAt: string;
  device?: {
    id: string;
    deviceName: string;
  } | null;
}

export interface SyncStatus {
  lastSync: string | null;
  nextScheduledSync: string | null;
  pendingRecords: number;
  enabledDevices: number;
  isRunning: boolean;
  progress: {
    status: 'idle' | 'syncing' | 'processing' | 'completed' | 'error';
    currentDevice?: string;
    totalDevices: number;
    processedDevices: number;
    totalRecords: number;
    newRecords: number;
    startTime?: string;
    endTime?: string;
    error?: string;
  };
}

export interface SyncResult {
  success: boolean;
  totalDevices: number;
  successfulDevices: number;
  totalRecordsFetched: number;
  totalRecordsNew: number;
  deviceResults: Array<{
    deviceId: string;
    deviceName: string;
    success: boolean;
    recordsFetched: number;
    recordsNew: number;
    message?: string;
    error?: string;
  }>;
  processResult?: {
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ zkUserId: string; date: string; error: string }>;
  };
  error?: string;
}

export interface RawAttendanceStats {
  total: number;
  synced: number;
  pending: number;
  unmapped: number;
  todayCount: number;
}

// ============ Device API ============

export const zkDeviceApi = {
  // Get all devices
  // Note: apiService returns parsed JSON directly (not wrapped in .data like axios)
  getAll: async (includeDisabled = false): Promise<ZkDevice[]> => {
    const response: any = await apiService.get(`/zk/devices?includeDisabled=${includeDisabled}`);
    return response.data;
  },

  // Get a single device
  getById: async (id: string): Promise<ZkDevice> => {
    const response: any = await apiService.get(`/zk/devices/${id}`);
    return response.data;
  },

  // Create a new device
  create: async (data: {
    deviceName: string;
    ipAddress: string;
    port?: number;
    deviceSerial?: string;
    timezone?: string;
    syncInterval?: number;
    isEnabled?: boolean;
  }): Promise<ZkDevice> => {
    const response: any = await apiService.post('/zk/devices', data);
    return response.data;
  },

  // Update a device
  update: async (id: string, data: Partial<{
    deviceName: string;
    ipAddress: string;
    port: number;
    deviceSerial: string;
    timezone: string;
    syncInterval: number;
    isEnabled: boolean;
  }>): Promise<ZkDevice> => {
    const response: any = await apiService.put(`/zk/devices/${id}`, data);
    return response.data;
  },

  // Delete a device
  delete: async (id: string): Promise<void> => {
    await apiService.delete(`/zk/devices/${id}`);
  },

  // Test connection for existing device
  testConnection: async (id: string): Promise<{ success: boolean; message: string; info?: ZkDeviceInfo }> => {
    const response: any = await apiService.post(`/zk/devices/${id}/test`);
    // Backend returns { success, message, data } where data is the info
    return {
      success: response.success,
      message: response.message,
      info: response.data
    };
  },

  // Test connection by IP/port (before saving)
  testConnectionByAddress: async (ipAddress: string, port = 4370): Promise<{ success: boolean; message: string; info?: ZkDeviceInfo }> => {
    const response: any = await apiService.post('/zk/devices/test-connection', { ipAddress, port });
    // Backend returns { success, message, data } where data is the info
    return {
      success: response.success,
      message: response.message,
      info: response.data
    };
  },

  // Enable/disable device
  setEnabled: async (id: string, isEnabled: boolean): Promise<ZkDevice> => {
    const response: any = await apiService.put(`/zk/devices/${id}/enabled`, { isEnabled });
    return response.data;
  },

  // Get users from device
  getUsers: async (id: string): Promise<ZkUser[]> => {
    const response: any = await apiService.get(`/zk/devices/${id}/users`);
    return response.data;
  },

  // Sync users from device to mappings
  syncUsers: async (deviceId: string): Promise<{ users: ZkUser[]; syncResult: { created: number; existing: number } }> => {
    const response: any = await apiService.post(`/zk/devices/${deviceId}/sync-users`);
    return response.data;
  }
};

// ============ Mapping API ============
// Note: apiService returns parsed JSON directly (not wrapped in .data like axios)

export const zkMappingApi = {
  // Get all mappings (optionally filtered by device)
  getAll: async (deviceId?: string): Promise<ZkEmployeeMapping[]> => {
    const url = deviceId ? `/zk/mappings?deviceId=${deviceId}` : '/zk/mappings';
    const response: any = await apiService.get(url);
    return response.data;
  },

  // Create a mapping
  create: async (data: {
    deviceId: string;
    zkUserId: string;
    zkUserName?: string;
    employeeId?: string | null;
    isActive?: boolean;
  }): Promise<ZkEmployeeMapping> => {
    const response: any = await apiService.post('/zk/mappings', data);
    return response.data;
  },

  // Update a mapping
  update: async (id: string, data: {
    zkUserName?: string;
    employeeId?: string | null;
    isActive?: boolean;
  }): Promise<ZkEmployeeMapping> => {
    const response: any = await apiService.put(`/zk/mappings/${id}`, data);
    return response.data;
  },

  // Delete a mapping
  delete: async (id: string): Promise<void> => {
    await apiService.delete(`/zk/mappings/${id}`);
  },

  // Bulk upsert mappings
  bulkUpsert: async (data: {
    deviceId: string;
    mappings: Array<{
      zkUserId: string;
      zkUserName?: string;
      employeeId?: string | null;
      employeeCode?: string | null;
    }>;
  }): Promise<{ created: number; updated: number }> => {
    const response: any = await apiService.post('/zk/mappings/bulk', data);
    return response.data;
  },

  // Auto-map by matching IDs
  autoMap: async (deviceId: string): Promise<{ mapped: number; unmapped: number }> => {
    const response: any = await apiService.post('/zk/mappings/auto-map', { deviceId });
    return response.data;
  }
};

// ============ Sync API ============

export const zkSyncApi = {
  // Sync all devices
  syncAll: async (processToHr = true): Promise<SyncResult> => {
    const response: any = await apiService.post('/zk/sync', { processToHr });
    return response.data;
  },

  // Sync single device
  syncDevice: async (deviceId: string, processToHr = true): Promise<SyncResult> => {
    const response: any = await apiService.post(`/zk/sync/${deviceId}`, { processToHr });
    return response.data;
  },

  // Process raw attendance to HR records
  processToHR: async (): Promise<{
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ zkUserId: string; date: string; error: string }>;
  }> => {
    const response: any = await apiService.post('/zk/sync/process-hr');
    return response.data;
  },

  // Get sync status
  getStatus: async (): Promise<SyncStatus> => {
    const response: any = await apiService.get('/zk/sync/status');
    return response.data;
  },

  // Get sync history
  getHistory: async (options?: { limit?: number; deviceId?: string }): Promise<ZkSyncHistory[]> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.deviceId) params.append('deviceId', options.deviceId);
    const url = `/zk/sync/history${params.toString() ? `?${params}` : ''}`;
    const response: any = await apiService.get(url);
    return response.data;
  }
};

// ============ Raw Attendance API ============

export const zkAttendanceApi = {
  // Get raw attendance records
  getAll: async (filter?: {
    deviceId?: string;
    zkUserId?: string;
    employeeId?: string;
    startDate?: string;
    endDate?: string;
    syncedToHr?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ZkRawAttendance[]> => {
    const params = new URLSearchParams();
    if (filter?.deviceId) params.append('deviceId', filter.deviceId);
    if (filter?.zkUserId) params.append('zkUserId', filter.zkUserId);
    if (filter?.employeeId) params.append('employeeId', filter.employeeId);
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);
    if (filter?.syncedToHr !== undefined) params.append('syncedToHr', String(filter.syncedToHr));
    if (filter?.limit) params.append('limit', String(filter.limit));
    if (filter?.offset) params.append('offset', String(filter.offset));
    const url = `/zk/attendance/raw${params.toString() ? `?${params}` : ''}`;
    const response: any = await apiService.get(url);
    return response.data;
  },

  // Get statistics (accepts optional date filter for performance)
  getStats: async (filter?: { startDate?: string; endDate?: string }): Promise<RawAttendanceStats> => {
    const params = new URLSearchParams();
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);
    const url = `/zk/attendance/raw/stats${params.toString() ? `?${params}` : ''}`;
    const response: any = await apiService.get(url);
    return response.data;
  },

  // Delete records
  delete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response: any = await apiService.delete('/zk/attendance/raw', { data: { ids } });
    return response.data;
  },

  // Mark as synced
  markAsSynced: async (ids: string[]): Promise<{ marked: number }> => {
    const response: any = await apiService.post('/zk/attendance/raw/mark-synced', { ids });
    return response.data;
  }
};

// ============ Register Employee to ZK Devices ============

export interface DeviceRegisterResult {
  deviceId: string;
  deviceName: string;
  status: 'ok' | 'skipped' | 'failed';
  action?: 'created' | 'updated' | 'already_registered';
  error?: string;
}

export interface RegisterEmployeeResult {
  success: boolean;
  employee: { id: string; name: string; employeeId: string };
  zkUserId: string;
  devices: DeviceRegisterResult[];
}

export const zkRegisterApi = {
  registerEmployee: async (employeeId: string): Promise<RegisterEmployeeResult> => {
    const response: any = await apiService.post(
      `/zk/employees/${employeeId}/register-to-devices`,
      {}
    );
    return response.data;
  },

  unregisterEmployee: async (employeeId: string): Promise<RegisterEmployeeResult> => {
    const response: any = await apiService.delete(
      `/zk/employees/${employeeId}/unregister-from-devices`
    );
    return response.data;
  }
};

// Export all APIs as a single object
export const zkService = {
  devices: zkDeviceApi,
  mappings: zkMappingApi,
  sync: zkSyncApi,
  attendance: zkAttendanceApi,
  register: zkRegisterApi
};

export default zkService;
